/*
 * Choosing groups: two prominent answers and a rarely used way to choose any set.
 *
 * Most things a team owns are owned by the whole team or by one person, so those are the two cards a
 * person sees — **Team members** and **Only me** — and one press is the whole decision. Anything else
 * ("the designers and Anna") goes through **Choose groups**, a dialog around `GroupChecklist` — a
 * filter and two lists of check boxes, groups and people, because a person looks for "the
 * designers" and for "Anna" in different places (`groupOptions.ts`). While such a set is chosen, it stands where the third card
 * would be, marked as the current answer, with *Change* beside it: the page always shows what is
 * chosen, in words.
 *
 * The caller decides what may be chosen and hands the candidates in — the groups the reader may give
 * the thing to, personal groups included (`groupsApi.listPickable`). The picker offers exactly those
 * and nothing else: a card whose group is not a candidate is not drawn, which is how a reader who may
 * not name the team is never offered it. The list route hands back every active person's personal
 * group, guests' included, so a caller whose things a guest may not own leaves those out itself. The
 * value is a list of group ids; the dialog will not hand back an empty one.
 *
 * The two cards are native radio buttons in a fieldset — one tab stop, the arrows move between them,
 * a screen reader announces "1 of 2" — and the way to the dialog is a button *after* them rather than
 * a third radio: arrowing onto a radio selects it, and a radio that opened a dialog would open it
 * under a keyboard reader who was only passing by.
 *
 * A form whose rare settings live behind a disclosure of their own — the new AI chat's *More
 * settings* — draws the picker with `offerChoose={false}` and opens `ChooseGroupsDialog` from there:
 * the two cards stay where everybody sees them, the door to the rare answer moves to where the rare
 * answers are, and while such a set is chosen the picker still says so under the cards, with its
 * *Change*, so the form always says who the answer is.
 */

import { useId, useState } from "react"
import { Button, Dialog } from "../../ui/index.ts"
import { formatList } from "../i18n/format.ts"
import { t } from "../i18n/index.ts"
import type { GroupId, GroupView } from "../store/model.ts"
import { GroupChecklist, OptionChip, useGroupOptions } from "./GroupChecklist.tsx"
import { presetOf, type GroupOption } from "./groupOptions.ts"
import "./groups.css"

export interface GroupPickerProps {
	/** Every group the reader may choose, personal groups included; the caller's policy decides. */
	candidates: readonly GroupView[]
	/** The chosen groups' ids. */
	value: readonly GroupId[]
	onChange: (value: GroupId[]) => void
	/** What the choice is for, drawn as the set's caption: "Owners". */
	legend: string
	/**
	 * Whether the picker offers its own *Choose groups* door beside the two cards. False where the
	 * surrounding form opens `ChooseGroupsDialog` from a place of its own; a chosen set is still drawn
	 * with its *Change*. Default true.
	 */
	offerChoose?: boolean
}

export function GroupPicker({
	candidates,
	value,
	onChange,
	legend,
	offerChoose = true,
}: GroupPickerProps) {
	const options = useGroupOptions(candidates)
	const preset = presetOf(value, options)
	const radioName = useId()
	const chosenId = useId()
	const [choosing, setChoosing] = useState(false)

	const chosenNames = formatList(
		value.flatMap((id) => {
			const option = options.byId.get(id)
			return option === undefined ? [] : [option.label]
		}),
	)
	const custom = preset === "custom"

	return (
		<fieldset className="tb-choices tb-group-picker">
			<legend className="tb-choices__legend">{legend}</legend>
			{options.team === undefined ? null : (
				<PresetCard
					option={options.team}
					hint={t("groups.choose.team.hint", { count: options.team.group.memberIds.length })}
					name={radioName}
					checked={preset === "team"}
					onChange={onChange}
				/>
			)}
			{options.mine === undefined ? null : (
				<PresetCard
					option={options.mine}
					hint={t("groups.choose.me.hint")}
					name={radioName}
					checked={preset === "mine"}
					onChange={onChange}
				/>
			)}
			{/* One element in both states, so that the focus the dialog hands back on close finds the
			    button it left, whichever of its two labels it now wears. */}
			{custom || offerChoose ? (
				<div className={custom ? "tb-group-picker__chosen" : "tb-group-picker__more"}>
					{custom ? (
						<span id={chosenId}>{t("groups.choose.chosen", { names: chosenNames })}</span>
					) : null}
					<Button
						variant={custom ? "secondary" : "ghost"}
						size={custom ? "sm" : "md"}
						label={t(custom ? "groups.choose.change" : "groups.choose.title")}
						{...(custom ? { "aria-describedby": chosenId } : {})}
						onClick={() => setChoosing(true)}
					/>
				</div>
			) : null}
			<ChooseGroupsDialog
				open={choosing}
				value={value}
				candidates={candidates}
				onClose={() => setChoosing(false)}
				onApply={(chosen) => {
					setChoosing(false)
					onChange(chosen)
				}}
			/>
		</fieldset>
	)
}

/** One of the two prominent answers: a card with the group's face, its name and what it means. */
function PresetCard({
	option,
	hint,
	name,
	checked,
	onChange,
}: {
	option: GroupOption
	hint: string
	name: string
	checked: boolean
	onChange: (value: GroupId[]) => void
}) {
	// The card is named by its group and described by its hint, so a screen reader reads "Team
	// members, radio button" and then what the choice means, not one run-on name.
	const id = useId()
	return (
		<label className="tb-choice tb-group-picker__card">
			<input
				type="radio"
				name={name}
				checked={checked}
				onChange={() => onChange([option.group.id])}
				aria-label={option.label}
				aria-describedby={`${id}-detail`}
			/>
			<OptionChip option={option} detail={hint} detailId={`${id}-detail`} />
		</label>
	)
}

export interface ChooseGroupsDialogProps {
	open: boolean
	/** The set chosen now; the dialog's working copy starts from it each time it opens. */
	value: readonly GroupId[]
	/** Every group the reader may choose, as the picker's own `candidates`. */
	candidates: readonly GroupView[]
	/** *Use these*: never called with an empty set. */
	onApply: (chosen: GroupId[]) => void
	/** Cancel, Escape or the close button: the chosen set stays as it was. */
	onClose: () => void
	/**
	 * Whether the dialog says so when the reader is in none of the ticked groups. True for a thing the
	 * reader would lose sight of (an AI chat); false where the reader keeps it whatever its groups are —
	 * a channel's member reads the channel without being in an owner group. Default true.
	 */
	warnWhenNotIn?: boolean
}

/**
 * The dialog behind *Choose groups*: `GroupChecklist` and the two buttons. What is ticked is a draft
 * until *Use these* — Cancel and Escape leave the chosen set as it was — and the draft starts from the
 * set that is chosen now each time the dialog opens. It is exported for the places that open it from
 * a door of their own: a form's *More settings*, an access request's "add as owners", an
 * administrator's rescue of a thing nobody owns any more, a channel's *Change groups*.
 */
export function ChooseGroupsDialog({
	open,
	value,
	candidates,
	onApply,
	onClose,
	warnWhenNotIn = true,
}: ChooseGroupsDialogProps) {
	// The working copy of the set; null while the dialog is shut. Started from `value` on the render
	// that opens it — the documented way to derive state from a prop — so it is never a frame late.
	const [draft, setDraft] = useState<GroupId[] | null>(null)
	if (open && draft === null) setDraft([...value])
	if (!open && draft !== null) setDraft(null)
	const ticked = draft ?? []

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (!next) onClose()
			}}
			title={t("groups.choose.title")}
			description={t("groups.choose.lead")}
			actions={
				<>
					<Button variant="neutral" label={t("action.cancel")} onClick={onClose} />
					<Button
						variant="primary"
						label={t("groups.choose.apply")}
						disabled={ticked.length === 0}
						onClick={() => onApply(ticked)}
					/>
				</>
			}
		>
			{/* Mounted only while open, so the filter starts empty each time the dialog does. */}
			{open ? (
				<GroupChecklist
					candidates={candidates}
					value={ticked}
					onChange={setDraft}
					legend={t("groups.choose.title")}
					legendHidden
					warnWhenNotIn={warnWhenNotIn}
				/>
			) : null}
		</Dialog>
	)
}
